import { useCallback, useEffect, useRef, useState } from 'react'
import RichTextField, { Props as RichTextFieldProps } from '.'
import { GridApi, Column } from 'ag-grid-community'
import { CustomCellEditorProps } from 'ag-grid-react'
import { addEventListenerForLastRow } from '../../../..'

interface OwnProps {
  parseValue?: (value: any) => any
  parseInitialValue?: (value: any) => any
  formatValue?: (value: any) => any
  api: GridApi
  eventKey: string | null
  rowIndex: number
  column: Column
}

type Props = OwnProps &
  RichTextFieldProps &
  Pick<CustomCellEditorProps, 'onValueChange'>

const createInitialValue = (props: Props): string => {
  const isEditable = () => {
    if (props.disabled) return !props.disabled
    const editable = props['colDef'].editable
    return typeof editable === 'function' ? editable(props) : editable
  }

  if (!isEditable()) {
    return props.value
  }
  if (props.eventKey === 'Backspace' || props.eventKey === 'Delete') {
    return ''
  }
  if (props.eventKey?.length === 1) {
    return props.eventKey
  }
  return props.parseInitialValue
    ? props.parseInitialValue(props.value)
    : props.value
}

const TextCell = (props: Props) => {
  const [value, setValue] = useState(() => createInitialValue(props))
  const [hasError, setHasError] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const valueRef = useRef(value)
  const hasErrorRef = useRef(hasError)

  const isEditable = () => {
    if (props.disabled) return !props.disabled
    const editable = props['colDef'].editable
    return typeof editable === 'function' ? editable(props) : editable
  }

  const notifyValueChange = useCallback(
    (val: string, error?: boolean) => {
      const isError = error ?? hasErrorRef.current
      if (isError) {
        props.onValueChange('')
      } else {
        props.onValueChange(props.parseValue ? props.parseValue(val) : val)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RichTextField(PureComponent)への参照安定化のため意図的に空。props はセル編集中に変化しない。値は ref 経由で参照
    []
  )

  useEffect(() => {
    if (inputRef.current && isEditable()) {
      inputRef.current.focus()
      inputRef.current.selectionStart = inputRef.current.selectionEnd =
        inputRef.current.value.length
      inputRef.current.scrollTop = inputRef.current.scrollHeight
      addEventListenerForLastRow(inputRef.current, props)
    }
    if (props.eventKey === 'Delete' || props.eventKey === 'Backspace') {
      props.api.clearFocusedCell()
      props.api.setFocusedCell(props.rowIndex, props.column)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時のみ実行。セルエディタの初期化処理
  }, [])

  // Reactive mode: 初期値が元の値と異なる場合（Backspace/Delete/1文字キー）AG Grid に通知
  useEffect(() => {
    if (valueRef.current !== props.value) {
      notifyValueChange(valueRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時のみ実行。初期値が props.value と異なる場合の通知
  }, [])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      valueRef.current = newValue
      setValue(newValue)
      notifyValueChange(newValue)
    },
    [notifyValueChange]
  )

  const handleBlur = useCallback(() => {
    props.api.stopEditing()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RichTextField(PureComponent)への参照安定化のため意図的に空。props.api はセル編集中に変化しない
  }, [])

  const handleSetError = useCallback(
    (error: boolean) => {
      hasErrorRef.current = error
      setHasError(error)
      notifyValueChange(valueRef.current, error)
    },
    [notifyValueChange]
  )

  return (
    <RichTextField
      value={value}
      disabled={!isEditable()}
      inputRef={inputRef}
      data={props.data}
      uiMeta={props.uiMeta}
      viewMeta={props.viewMeta}
      onChange={handleChange}
      onBlur={handleBlur}
      setError={handleSetError}
    />
  )
}

export default TextCell
